import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleInfo,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

/**
 * NoticeBanner — an inline, non-dismissible message about the screen it sits
 * on: why a list could not load, why an action is unavailable, what an action
 * is about to do.
 *
 * Not a toast and not a modal: a toast disappears, and this is a standing fact
 * about what the user is looking at. Not `TabEmptyState` either — that fills a
 * body that has nothing in it, while this sits above or beside content.
 *
 * Existed as ad-hoc markup in several modules (the price-quotation prefill note
 * on the sales-invoice form, the Ejar register's unreachable message) with a
 * slightly different border, radius and text size each time.
 *
 * @param {"info"|"warning"|"danger"|"success"} tone - default "info"
 * @param {React.ReactNode} children - the message
 * @param {React.ReactNode} icon - overrides the tone's icon
 * @param {string} className - appended
 */
const TONES = {
  info: {
    className: "border-info/20 bg-info-bg-soft text-info",
    icon: faCircleInfo,
  },
  warning: {
    className: "border-warning-border bg-warning-light text-warning-text",
    icon: faTriangleExclamation,
  },
  danger: {
    className: "border-danger/20 bg-danger-bg-soft text-danger",
    icon: faTriangleExclamation,
  },
  success: {
    className: "border-success/20 bg-success-bg-soft text-success",
    icon: faCircleCheck,
  },
};

export const NOTICE_TONE = {
  INFO: "info",
  WARNING: "warning",
  DANGER: "danger",
  SUCCESS: "success",
};

const NoticeBanner = ({
  tone = NOTICE_TONE.INFO,
  children,
  icon,
  className = "",
}) => {
  // Never indexed bare: a tone typo would render an unstyled strip that reads as
  // a layout bug rather than as a message.
  const resolved = TONES[tone] ?? TONES[NOTICE_TONE.INFO];

  return (
    <div
      className={`rounded-lg border p-3 text-xs flex gap-2 ${resolved.className} ${className}`.trim()}
    >
      <FontAwesomeIcon
        icon={icon ?? resolved.icon}
        className="mt-0.5 shrink-0"
      />
      <span className="leading-relaxed">{children}</span>
    </div>
  );
};

export default NoticeBanner;
